import { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch } from "react-redux";
import ViewUserModal from "./ViewUserModal";
import UserFormModal from "./UserFormModal";
import FilterUserModal from "./FilterUserModal";
import { getAdminUsersApi, clearFilteredChips, setInitAdminUserDetail } from "../../../redux/features/adminUserSlice";
import { updateUser } from "../../../services/Admin";
import { errorToast, successToast } from "../../Toasts/Toasts";
import { chipName } from "../../../utils/sharedFunctions";
import moment from "moment";

const UsersGrid = ({
  filteredChips,
  handleChipRemove,
  adminUserDetails,
  currentSize,
  setCurrentSize,
  masterData,
  setId,
  tebleLoading,
}) => {
  const dispatch = useDispatch();
  const [hasMoreData, setHasMoreData] = useState(true);
  const [loading, setLoading] = useState(false);
  const [currentResultSetLength, setCurrentResultSetLength] = useState(0);


  const [showViewUser, setShowViewUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditUser, setShowEditUser] = useState(false);
  const [editUserData, setEditUserData] = useState(null);

  let page = 1;

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowViewUser(true);
  };

  const handleCloseViewModal = () => {
    setShowViewUser(false);
    setSelectedUser(null);
  };

  const handleEditUser = (user) => {
    setEditUserData(user);
    setShowEditUser(true);
  };

  const handleStatusToggle = async (user) => {
    try {
      const newActiveState = user.status !== 1;
      const response = await updateUser(user.id, { is_active: newActiveState });

      if (response?.success) {
        successToast(response?.message || "Status updated successfully");
        dispatch(getAdminUsersApi({ filteredData: {}, page: 1, size: currentSize }));
      } else {
        errorToast(response?.message || "Failed to update status");
      }
    } catch (error) {
      errorToast(error?.response?.data?.message || "Something went wrong while updating status");
    }
  };

  const loadMoreData = useCallback(async () => {
    if (loading || !hasMoreData) return;
    setLoading(true);

    let filterData = Object.fromEntries(filteredChips ?? []);
    let transformedChips = {};

    for (let key in filterData) {
      if (typeof filterData[key] === "object" && "label" in filterData[key] && "value" in filterData[key]) {
        transformedChips[key] = filterData[key].value;
      } else {
        transformedChips[key] = filterData[key];
      }
    }

    const response = await dispatch(
      getAdminUsersApi({ filteredData: transformedChips, page, size: currentSize + 10 })
    );

    if (response?.meta?.requestStatus === "fulfilled") {
      const newData = response.payload?.data;
      if (newData?.length > 0 && newData?.length > currentResultSetLength) {
        setCurrentSize(currentSize + 10);
        setCurrentResultSetLength(newData?.length);
      } else {
        setHasMoreData(false);
      }
    } else {
      errorToast("Error occurred while loading more data");
    }
    setLoading(false);
    // eslint-disable-next-line
  }, [loading, hasMoreData, currentSize, setCurrentSize, currentResultSetLength, dispatch, filteredChips]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollContainer = document.getElementById("adminUsersComponent");
      if (
        scrollContainer &&
        scrollContainer.scrollHeight - scrollContainer.scrollTop === scrollContainer.clientHeight
      ) {
        loadMoreData();
      }
    };

    const scrollContainer = document.getElementById("adminUsersComponent");
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handleScroll);
      }
    };
    // eslint-disable-next-line
  }, [loadMoreData]);

  useEffect(() => {
    return () => {
      dispatch(clearFilteredChips());
      dispatch(setInitAdminUserDetail());
    };
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <div className="card">
        <div className="card-header pb-3 pt-3 border-bottom d-flex justify-content-between">
          <h5 className="card-title mb-0">Details</h5>
          <div className="d-flex align-items-center gap-2">
            {/* Filter Chips */}
            <div className="filter-card" style={{ display: "block" }}>
              {filteredChips?.map(
                ([key, value]) =>
                  key !== "user_id" && (
                    <div className="chip" key={key}>
                      <input type="checkbox" name="filters[]" defaultValue="a" defaultChecked="" readOnly />
                      <span>
                        {chipName(key)} {":"}{" "}
                        {key === "is_active" || key === "role_id" || key === "onboarding_date" ? value?.label : value}
                      </span>
                      <button type="button" className="zap">
                        <i className="ti ti-x fs-6" onClick={() => handleChipRemove(key)} />
                      </button>
                    </div>
                  )
              )}
            </div>
            <FilterUserModal currentSize={currentSize} filteredChips={filteredChips} />
          </div>
        </div>

        <InfiniteScroll
          dataLength={adminUserDetails?.length ?? 0}
          next={loadMoreData}
          hasMore={hasMoreData}
          loader={loading ? <h4>Loading...</h4> : null}
          scrollableTarget="adminUsersComponent"
        >
          <div
            id="adminUsersComponent"
            className="card-datatable table-responsive"
            style={{ height: "500px", overflowY: "auto" }}
          >
            <table className="table" id="usersTable">
              <thead className="fixed-table-header">
                <tr className="align-baseline">
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Address</th>
                  <th>Onboarding Date</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {tebleLoading ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      Loading.....
                    </td>
                  </tr>
                ) : adminUserDetails?.length !== 0 ? (
                  adminUserDetails?.map((user, index) => (
                    <tr key={index}>
                      <td>
                        <Link
                          to="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleViewUser(user);
                          }}
                        >
                          <div>{user?.name}</div>
                          <small className="text-muted">ID: {user?.id}</small>
                        </Link>
                      </td>
                      <td>{user?.phone_number}</td>
                      <td>{user?.email}</td>
                      <td>{user?.role || user?.["Role.name"]}</td>
                      <td>
                        <div>{user?.state || user?.["State.state_name"]},</div>
                        <small className="text-muted">{user?.country_name || user?.["Country.name"]}</small>
                      </td>
                      <td>{user?.onboarding_date ? moment(user.onboarding_date).format("DD MMM YY") : "—"}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <input
                            className="input-switch"
                            type="checkbox"
                            id={`status-${index}`}
                            checked={user?.status === 1}
                            onChange={() => handleStatusToggle(user)}
                          />
                          <label className="label-switch" htmlFor={`status-${index}`} />
                          <span className="info-text ms-2" />
                        </div>
                      </td>
                      <td>
                        <div className="btn-group" role="group" aria-label="Actions">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-actions waves-effect"
                            onClick={() => handleEditUser(user)}
                          >
                            <i className="ti ti-edit text-blue"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className="pt-3 ps-3">
                    <td colSpan="8">No record found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </InfiniteScroll>
      </div>

      <ViewUserModal show={showViewUser} onClose={handleCloseViewModal} user={selectedUser} />
      {editUserData && (
        <UserFormModal
          show={showEditUser}
          setShow={setShowEditUser}
          userData={editUserData}
          isEdit={true}
          masterData={masterData}
          setId={setId}
          currentSize={currentSize}
        />
      )}
    </>
  );
};

export default UsersGrid;
